
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Settings, Save, DollarSign, MessageSquare } from "lucide-react";

interface PrintablesSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  description: string;
}

const PrintablesSettingsManager = () => {
  const [settings, setSettings] = useState<PrintablesSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('printables_settings')
        .select('*')
        .order('setting_key');

      if (error) throw error;
      console.log('Fetched settings:', data);
      setSettings(data || []);
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Error",
        description: "Failed to load printables settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSettingChange = (settingKey: string, value: string) => {
    setSettings(prev => {
      const settingExists = prev.some(s => s.setting_key === settingKey);
      if (settingExists) {
        return prev.map(setting =>
          setting.setting_key === settingKey
            ? { ...setting, setting_value: value }
            : setting
        );
      }
      // Create new setting with proper description
      const descriptions: Record<string, string> = {
        'printables_description': 'Description text for printables page',
        'button_text_signed_in': 'Text for sign in button',
        'button_text_download': 'Text for download button',
        'premium_description': 'Description for premium access'
      };
      
      return [...prev, { 
        id: '', 
        setting_key: settingKey, 
        setting_value: value, 
        description: descriptions[settingKey] || `Description for ${settingKey}`
      }];
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare settings for upsert, removing empty id fields
      const settingsToUpsert = settings.map(({ id, ...rest }) => ({
        setting_key: rest.setting_key,
        setting_value: rest.setting_value,
        description: rest.description
      }));

      console.log('Upserting settings:', settingsToUpsert);

      const { error } = await supabase
        .from('printables_settings')
        .upsert(settingsToUpsert, { onConflict: 'setting_key' });
        
      if (error) {
        console.error('Upsert error:', error);
        throw error;
      }

      toast({
        title: "Success",
        description: "Printables settings updated successfully",
      });
      await fetchSettings();
    } catch (error: any) {
      console.error('Error updating settings:', error);
      toast({
        title: "Error",
        description: `Failed to update settings: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };


  const getSettingValue = (key: string) => {
    const setting = settings.find(s => s.setting_key === key);
    return setting?.setting_value || '';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-0">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-purple-50 border-b border-pink-200">
          <CardTitle className="flex items-center gap-3 text-pink-800">
            <Settings className="w-6 h-6" />
            Printables Settings Management
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">

          {/* Printables Description Setting */}
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-blue-800 text-lg">
                <MessageSquare className="w-5 h-5" />
                Printables Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="printables_description" className="text-blue-700">Printables Description Text</Label>
                <Textarea
                  id="printables_description"
                  value={getSettingValue('printables_description')}
                  onChange={(e) => handleSettingChange('printables_description', e.target.value)}
                  rows={3}
                  className="border-blue-300 focus:border-blue-500 bg-white"
                  placeholder="Enter description text that will appear on the printables page..."
                />
                <p className="text-xs text-blue-600 mt-1">
                  This text will be displayed prominently on the printables page below the main heading.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Button Text Settings */}
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-purple-800 text-lg">Button Texts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="button_text_signed_in" className="text-purple-700">Sign In Button Text</Label>
                <Input
                  id="button_text_signed_in"
                  type="text"
                  value={getSettingValue('button_text_signed_in')}
                  onChange={(e) => handleSettingChange('button_text_signed_in', e.target.value)}
                  className="border-purple-300 focus:border-purple-500 bg-white"
                />
              </div>
              <div>
                <Label htmlFor="button_text_download" className="text-purple-700">Download Button Text</Label>
                <Input
                  id="button_text_download"
                  type="text"
                  value={getSettingValue('button_text_download')}
                  onChange={(e) => handleSettingChange('button_text_download', e.target.value)}
                  className="border-purple-300 focus:border-purple-500 bg-white"
                />
              </div>
            </CardContent>
          </Card>

          {/* Description Setting */}
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-amber-800 text-lg">Description Text</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="premium_description" className="text-amber-700">Premium Description</Label>
                <Textarea
                  id="premium_description"
                  value={getSettingValue('premium_description')}
                  onChange={(e) => handleSettingChange('premium_description', e.target.value)}
                  rows={3}
                  className="border-amber-300 focus:border-amber-500 bg-white"
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrintablesSettingsManager;
